import { useField } from 'formik';
import { DatePicker } from '@kula-ai/components';
import dayjs from 'dayjs';

// This field carries education and work history, so the year grid has to reach
// back further than the picker's own 2013 default.
const EARLIEST_HISTORY_YEAR = 1950;

export const MonthYearField = ({ item, formik }) => {
  const {
    name,
    label,
    isRequired,
    initialValue,
    disabled = false,
    placeholder,
    min,
    max,
  } = item;

  const [formField, meta, helpers = {}] = useField(name);
  const { setValue, setTouched, setError } = helpers || {};
  const { touched } = meta;
  const value = formField?.value ?? meta?.value;
  const { submitCount } = formik || {};

  const showError = (submitCount > 0 || touched) && !value && isRequired;

  // Derive the picker value directly from the form state so it always reflects
  // formik — including async resume autofill that populates this dynamically-added
  // field after it has already mounted (a local state snapshot would stay stale).
  const resolved =
    value !== undefined && value !== null && value !== ''
      ? value
      : (initialValue ?? null);
  const selectedDate = resolved !== null ? new Date(resolved) : null;

  if (!item) return null;

  const handleOnChange = (date: Date) => {
    setValue?.(date);
    setTouched?.(true);
    setError?.(null);
  };

  return (
    // The configs' className/styleVersion styled react-datepicker's bare input
    // and would draw a second border around the picker's own; its `error` prop
    // covers what the error classes did.
    <DatePicker
      mode="month"
      value={selectedDate}
      onChange={handleOnChange}
      dateFormat="MM/yyyy"
      minDate={min ? dayjs(min).toDate() : undefined}
      maxDate={max ? dayjs(max).toDate() : undefined}
      disabled={disabled}
      error={!!showError}
      placeholder={placeholder || `Select ${label}`}
      // The year grid opens on the page holding the value, clamped into this
      // window — so the floor has to reach back far enough for education and
      // work history, not the package default of 2013.
      startYear={min ? dayjs(min).year() : EARLIEST_HISTORY_YEAR}
      endYear={max ? dayjs(max).year() : undefined}
    />
  );
};
